/* ========================= AVATAR UPLOADER ========================= */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Camera, ImagePlus, UserRound } from "lucide-react";
import { getUploadErrorMessage, uploadImageToImgbb } from "../utils/uploadImageToImgbb";

/**
 * AvatarUploader
 * ----------------
 * Props:
 *  - initialUrl: (string) optional preview URL
 *  - onUpload: (function) called with (uploadedUrl) after a successful upload
 *  - disabled: (boolean) disables input
 *  - maxWidth: (number) width to resize images to (px)
 *  - ariaLabel: (string) accessible label for input
 *  - uploadFn: (optional) injection for testing: async function(formData) => { success, data }
 *
 * Notes:
 *  - Uses canvas to resize client-side (improve low-bandwidth UX)
 *  - Uses fetch and expects IMGBB-compatible response
 *  - Does NOT embed any API key in source code
 */
export function AvatarUploader({
  initialUrl,
  onUpload,
  disabled = false,
  maxWidth = 1200,
  ariaLabel = "Upload an avatar",
  uploadFn,
  label = "Profile photo",
  resizedHint = "Images are resized on-device to save bandwidth.",
  uploadingLabel = "Uploading...",
  showLabel = true,
  compact = false,
  choosePhotoLabel = "Choose photo",
  changePhotoLabel = "Change photo",
  currentPhotoLabel = "Current profile photo",
  noFileLabel = "No file selected",
}) {
  const [preview, setPreview] = useState(initialUrl || null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreview(initialUrl || null);
    setImageLoadFailed(false);
  }, [initialUrl]);

  const resizeFile = (file, maxSize = maxWidth) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let { width, height } = img;
          if (width > maxSize) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          // Export as JPEG to keep size small on mobile
          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error("Failed to compress image"));
              resolve(blob);
            },
            "image/jpeg",
            0.8
          );
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageLoadFailed(false);

    if (!file.type?.startsWith("image/")) {
      setError("Please select a valid image file.");
      return;
    }

    setError(null);
    setUploading(true);
    setProgress(5);

    try {
      const compressedBlob = await resizeFile(file, maxWidth);
      const dataUrl = await blobToDataUrl(compressedBlob);
      setPreview(dataUrl);
      setProgress(20);

      const base64 = dataUrl.split(",")[1];

      const uploadFile = new File(
        [compressedBlob],
        (file.name || "avatar").replace(/\.[^/.]+$/, "") + ".jpg",
        { type: "image/jpeg" }
      );
      const uploader = uploadFn || uploadImageToImgbb;
      const uploaded = await uploader(uploadFile || base64);

      setProgress(90);
      const uploadedUrl = typeof uploaded === "string" ? uploaded : uploaded?.data?.url || uploaded?.url;
      if (!uploadedUrl) throw new Error("Image service returned an invalid response.");

      setProgress(100);
      setUploading(false);
      setProgress(null);

      if (onUpload) onUpload(uploadedUrl);
    } catch (err) {
      setError(getUploadErrorMessage(err));
      setUploading(false);
      setProgress(null);
    }
  };

  const openPicker = () => {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  };

  const hasPreview = !!preview && !imageLoadFailed;

  const avatarSizeClass = compact
    ? "w-16 h-16 min-w-16 min-h-16 sm:w-20 sm:h-20 sm:min-w-20 sm:min-h-20"
    : "w-20 h-20 min-w-20 min-h-20 sm:w-24 sm:h-24 sm:min-w-24 sm:min-h-24";

  return (
    <div className="avatar-uploader">
      {showLabel && <label className="block text-sm font-medium mb-2">{label}</label>}
      <div className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50/70 dark:bg-neutral-800/40 p-3 text-center">
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled || uploading}
          className={`${avatarSizeClass} relative overflow-hidden rounded-full border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 flex items-center justify-center group transition focus:outline-none focus:ring-2 focus:ring-green-600 disabled:opacity-70 disabled:cursor-not-allowed`}
          aria-label={ariaLabel}
        >
          {hasPreview ? (
            <img
              src={preview}
              alt="Avatar preview"
              className="w-full h-full object-cover"
              onError={() => setImageLoadFailed(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 dark:from-emerald-900/30 dark:to-green-900/20 text-emerald-700 dark:text-emerald-300">
              <UserRound className="w-7 h-7" aria-hidden="true" />
            </div>
          )}

          <span className="absolute -bottom-0.5 -right-0.5 p-1 rounded-full bg-emerald-600 text-white shadow-sm ring-2 ring-white dark:ring-neutral-800">
            <Camera className="w-3.5 h-3.5" aria-hidden="true" />
          </span>
        </button>

        <div className="w-full flex flex-col items-center">
          <input
            ref={fileInputRef}
            aria-label={ariaLabel}
            type="file"
            accept="image/*"
            onChange={handleFile}
            disabled={disabled || uploading}
            className="sr-only"
          />

          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={openPicker}
              disabled={disabled || uploading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-neutral-900 border border-gray-300 dark:border-neutral-600 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-neutral-800 transition disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <ImagePlus className="w-4 h-4" aria-hidden="true" />
              {hasPreview ? changePhotoLabel : choosePhotoLabel}
            </button>

            <span className="sr-only" aria-live="polite">
              {hasPreview ? currentPhotoLabel : noFileLabel}
            </span>
          </div>

          {uploading && (
            <div className="mt-2 space-y-1 w-full max-w-xs">
              <div className="text-xs text-emerald-700 dark:text-emerald-300">
                {uploadingLabel} {progress ? `${Math.round(progress)}%` : ""}
              </div>
              <div className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-neutral-700 overflow-hidden">
                <div
                  className="h-full bg-emerald-600 transition-all duration-200"
                  style={{ width: `${Math.max(8, progress || 0)}%` }}
                />
              </div>
            </div>
          )}

          {error && <div className="text-xs mt-2 text-red-600 dark:text-red-300">{error}</div>}

          <div className="text-xs mt-2 text-gray-500 dark:text-gray-400 text-center">{resizedHint}</div>
        </div>
      </div>
    </div>
  );
}

AvatarUploader.propTypes = {
  initialUrl: PropTypes.string,
  onUpload: PropTypes.func,
  disabled: PropTypes.bool,
  maxWidth: PropTypes.number,
  ariaLabel: PropTypes.string,
  uploadFn: PropTypes.func,
  label: PropTypes.string,
  resizedHint: PropTypes.string,
  uploadingLabel: PropTypes.string,
  showLabel: PropTypes.bool,
  compact: PropTypes.bool,
  choosePhotoLabel: PropTypes.string,
  changePhotoLabel: PropTypes.string,
  currentPhotoLabel: PropTypes.string,
  noFileLabel: PropTypes.string,
};